import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Truck, PackageCheck, Ban, Save, History } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import {
  adjustPurchaseOrderLines,
  getPurchaseOrders,
  receivePurchaseOrder,
  updatePurchaseOrderStatus,
} from '../../services/api';
import { can, formatQuantity } from '../../lib/inventory';
import {
  PURCHASE_ORDER_STATUS as STATUS,
  orderedQuantity,
  remainingQuantity,
  toNumber,
} from '../../lib/purchaseOrders';

const FILTERS = {
  open: ['to_approve', 'ordered', 'partially_received'],
  to_approve: ['to_approve'],
  incoming: ['ordered', 'partially_received'],
  received: ['received'],
  cancelled: ['cancelled'],
  all: Object.keys(STATUS),
};

const dateLabel = (value) => (value ? new Date(value).toLocaleDateString('fr-CA') : '');

const PurchaseOrdersPage = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const [receipt, setReceipt] = useState({});

  const canManage = can(user, 'purchase_orders.manage');
  const canReceive = can(user, 'purchase_orders.receive');

  const load = useCallback(async () => {
    try {
      const response = await getPurchaseOrders();
      setOrders(Array.isArray(response.data) ? response.data : []);
      setDrafts({});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger les bons de commande');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (orderId, action, success) => {
    setBusy(orderId);
    try {
      await action();
      toast.success(success);
      await load();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Opération impossible');
      if (error.response?.status === 409) await load();
      return false;
    } finally {
      setBusy(null);
    }
  };

  const draftFor = (order) => drafts[order.id] || {};
  const setDraft = (order, lineId, value) =>
    setDrafts(current => ({ ...current, [order.id]: { ...draftFor(order), [lineId]: value } }));
  const hasDraft = (order) => Object.keys(draftFor(order)).length > 0;

  const saveQuantities = (order) => run(
    order.id,
    () => adjustPurchaseOrderLines(order.id, {
      lines: Object.entries(draftFor(order)).map(([line_id, quantity]) => ({
        line_id,
        quantity: String(quantity).replace(',', '.') || '0',
      })),
    }),
    'Quantités enregistrées',
  );

  const approve = async (order) => {
    if (hasDraft(order) && !(await saveQuantities(order))) return;
    await run(order.id, () => updatePurchaseOrderStatus(order.id, { status: 'ordered' }), 'Bon approuvé et commandé');
  };

  const cancel = (order) => {
    if (!window.confirm(`Annuler le bon de commande ${order.supplier}? Le stock ne sera pas modifié.`)) return;
    run(order.id, () => updatePurchaseOrderStatus(order.id, { status: 'cancelled' }), 'Bon annulé');
  };

  const openReceipt = (order) => {
    setReceiving(order);
    setReceipt(Object.fromEntries(order.lines.map(line => [line.line_id, remainingQuantity(line)])));
  };

  const submitReceipt = async () => {
    const receipts = Object.entries(receipt)
      .map(([line_id, quantity]) => ({ line_id, quantity: String(quantity).replace(',', '.') }))
      .filter(line => toNumber(line.quantity) > 0);
    if (!receipts.length) {
      toast.error('Indiquez au moins une quantité reçue');
      return;
    }
    const ok = await run(receiving.id, () => receivePurchaseOrder(receiving.id, { receipts }), 'Réception enregistrée; le stock est à jour');
    if (ok) setReceiving(null);
  };

  const visible = orders.filter(order => FILTERS[filter].includes(order.status));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">Bons de commande</h1>
          <p className="text-slate-500">Créés automatiquement quand un article atteint son seuil minimal</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="po-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Bons ouverts</SelectItem>
            <SelectItem value="to_approve">À approuver</SelectItem>
            <SelectItem value="incoming">En attente de réception</SelectItem>
            <SelectItem value="received">Reçus</SelectItem>
            <SelectItem value="cancelled">Annulés</SelectItem>
            <SelectItem value="all">Tous</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {visible.length === 0 ? (
        <Card><CardContent className="text-center py-12 text-slate-500">Aucun bon de commande</CardContent></Card>
      ) : visible.map(order => {
        const status = STATUS[order.status] || STATUS.to_approve;
        const editable = canManage && order.status === 'to_approve';
        const receivable = canReceive && ['ordered', 'partially_received'].includes(order.status);
        const cancellable = canManage && FILTERS.open.includes(order.status);
        return (
          <Card key={order.id} data-testid={`po-${order.id}`}>
            <CardContent className="p-0">
              <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-full bg-[#1a3a5c] flex items-center justify-center text-white">
                    <Truck className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a3a5c]">{order.supplier}</h3>
                    <p className="text-sm text-slate-500">
                      {order.lines.length} article(s) · mis à jour le {dateLabel(order.updated_at)}
                    </p>
                  </div>
                </div>
                <span className={`self-start sm:self-center px-3 py-1 rounded-full text-xs font-medium ${status.badge}`}>
                  {status.label}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left font-medium px-3 sm:px-4 py-2">Article</th>
                      <th className="hidden sm:table-cell text-right font-medium px-4 py-2 whitespace-nowrap">Stock / min.</th>
                      <th className="text-right font-medium px-3 sm:px-4 py-2">Commandé</th>
                      <th className="hidden sm:table-cell text-right font-medium px-4 py-2">Reçu</th>
                      <th className="text-right font-medium px-3 sm:px-4 py-2">Restant</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {order.lines.map(line => (
                      <tr key={line.line_id}>
                        <td className="px-3 sm:px-4 py-3">
                          <p className="font-medium text-[#1a3a5c]">{line.item_name}</p>
                          {line.ordered_quantity && line.ordered_quantity !== line.recommended_quantity && (
                            <p className="text-xs text-slate-400">Recommandé : {formatQuantity(line.recommended_quantity)} {line.unit}</p>
                          )}
                        </td>
                        <td className="hidden sm:table-cell px-4 py-3 text-right text-slate-500 whitespace-nowrap">
                          {formatQuantity(line.current_quantity)} / {formatQuantity(line.minimum_quantity)}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-right whitespace-nowrap">
                          {editable ? (
                            <Input
                              inputMode="decimal"
                              aria-label={`Quantité à commander : ${line.item_name}`}
                              className="w-20 sm:w-24 ml-auto text-right"
                              value={draftFor(order)[line.line_id] ?? formatQuantity(orderedQuantity(line))}
                              onChange={(event) => setDraft(order, line.line_id, event.target.value)}
                            />
                          ) : (
                            <span>{formatQuantity(orderedQuantity(line))} {line.unit}</span>
                          )}
                        </td>
                        <td className="hidden sm:table-cell px-4 py-3 text-right whitespace-nowrap">{formatQuantity(line.received_quantity || '0')}</td>
                        <td className="px-3 sm:px-4 py-3 text-right whitespace-nowrap">{formatQuantity(remainingQuantity(line))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(order.receipt_events || []).length > 0 && (
                <details className="px-4 py-3 border-t text-sm">
                  <summary className="cursor-pointer text-slate-600 flex items-center gap-2">
                    <History className="w-4 h-4" /> Historique des réceptions ({order.receipt_events.length})
                  </summary>
                  <ul className="mt-2 space-y-1 text-slate-500">
                    {order.receipt_events.map(event => (
                      <li key={event.id}>
                        {dateLabel(event.created_at)} — {event.lines.map(received => {
                          const line = order.lines.find(item => item.line_id === received.line_id);
                          return `${line?.item_name || 'Article'} : ${formatQuantity(received.quantity)}`;
                        }).join(', ')}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {(editable || receivable || cancellable) && (
                <div className="p-4 border-t flex flex-wrap gap-2 justify-end">
                  {editable && hasDraft(order) && (
                    <Button variant="outline" size="sm" disabled={busy === order.id} onClick={() => saveQuantities(order)}>
                      <Save className="w-4 h-4 mr-2" />Enregistrer les quantités
                    </Button>
                  )}
                  {cancellable && (
                    <Button variant="outline" size="sm" className="text-red-700" disabled={busy === order.id} onClick={() => cancel(order)}>
                      <Ban className="w-4 h-4 mr-2" />Annuler
                    </Button>
                  )}
                  {editable && (
                    <Button size="sm" className="bg-[#00b4d8] hover:bg-[#009bb8]" disabled={busy === order.id}
                      onClick={() => approve(order)} data-testid={`po-approve-${order.id}`}>
                      <Truck className="w-4 h-4 mr-2" />Approuver et commander
                    </Button>
                  )}
                  {receivable && (
                    <Button size="sm" className="bg-[#00b4d8] hover:bg-[#009bb8]" disabled={busy === order.id}
                      onClick={() => openReceipt(order)} data-testid={`po-receive-${order.id}`}>
                      <PackageCheck className="w-4 h-4 mr-2" />Enregistrer une réception
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={Boolean(receiving)} onOpenChange={(open) => !open && setReceiving(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Réception — {receiving?.supplier}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Inscrivez les quantités réellement reçues. Une réception partielle laisse le bon ouvert.
          </p>
          <div className="space-y-3">
            {receiving?.lines.filter(line => toNumber(remainingQuantity(line)) > 0).map(line => (
              <div key={line.line_id} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-[#1a3a5c] truncate">{line.item_name}</p>
                  <p className="text-xs text-slate-500">Restant : {formatQuantity(remainingQuantity(line))} {line.unit}</p>
                </div>
                <Input
                  inputMode="decimal"
                  aria-label={`Quantité reçue : ${line.item_name}`}
                  className="w-24 text-right"
                  value={receipt[line.line_id] ?? ''}
                  onChange={(event) => setReceipt(current => ({ ...current, [line.line_id]: event.target.value }))}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setReceiving(null)}>Fermer</Button>
            <Button className="bg-[#00b4d8] hover:bg-[#009bb8]" disabled={busy === receiving?.id} onClick={submitReceipt}>
              Enregistrer la réception
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrdersPage;
